
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import xmlFormat from 'xml-formatter';
import { xmlToJson } from '../utils/conversionUtils';
import SmartEditor from '../components/SmartEditor';
import Toolbar from '../components/Toolbar'; // Import Toolbar
import { useTranslation } from '../hooks/useTranslation'; // Import hook
import '../components/JsonWorkspace.css';
import { ArrowLeft, Code2 } from 'lucide-react';

const XmlEditor = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [input, setInput] = useState(location.state?.code || '');
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    useEffect(() => {
        if (successMsg) {
            const timer = setTimeout(() => setSuccessMsg(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [successMsg]);

    // Auto-format on mount if input comes from router state
    useEffect(() => {
        if (location.state?.code) {
            handleFormat(); // Call handleFormat directly
        }
    }, []);

    const handleFormat = () => {
        // Removed the !input.trim() check to allow formatting empty string if desired, or let xmlFormat handle it.
        try {
            const formatted = xmlFormat(input, {
                indentation: '  ',
                filter: (node) => node.type !== 'Comment',
                collapseContent: true,
                lineSeparator: '\n'
            });
            setInput(formatted);
            setSuccessMsg('Formatted XML'); // Updated success message
            setError(null);
        } catch (err) {
            setError('Format failed: ' + err.message); // Updated error message
        }
    };

    const handleClear = () => {
        setInput('');
        setError(null); // Clear error on clear
        setSuccessMsg('Cleared'); // Set success message on clear
    };

    const handleCopy = async () => { // Made async
        try {
            await navigator.clipboard.writeText(input);
            setSuccessMsg('Copied to clipboard');
        } catch (err) {
            setError('Failed to copy'); // Added error handling for copy
        }
    };

    const handleConvert = () => {
        try {
            const json = xmlToJson(input);
            navigate('/json', { state: { code: json } });
        } catch (err) {
            setError('Conversion failed: ' + err.message);
        }
    };

    return (
        <div className="workspace">
            <Toolbar
                onFormat={handleFormat}
                onClear={handleClear}
                onCopy={handleCopy}
                onConvert={handleConvert}
                convertLabel={t('convertJson')}
                formatLabel={t('format')}
                clearLabel={t('clear')}
                copyLabel={t('copy')}
            >
                <Link to="/" style={{
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginRight: '12px',
                    transition: 'opacity 0.2s'
                }}
                    onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
                    onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                    title="Back to Home"
                >
                    <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        background: 'var(--accent-gradient)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: 'var(--shadow-glow)'
                    }}>
                        <Code2 size={20} color="#fff" />
                    </div>
                </Link>
            </Toolbar>

            <div className="workspace-content">
                <div className="panel">
                    <SmartEditor value={input} onChange={setInput} language="xml" />
                </div>
            </div>

            <AnimatePresence>
                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="status-bar error"
                    >
                        {error}
                    </motion.div>
                )}
                {successMsg && !error && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="status-bar success"
                    >
                        {successMsg}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default XmlEditor;
